import { FC, useContext, useEffect, useRef, useState } from 'react';
import { getStyles } from './AddSupportingDocuments.style';
import { Box, Button, Stack, Typography } from '@mui/material';
import { LocalizationContext } from '@context/LocalizationContext';
import UploadFromComputerComponent from './UploadFromComputer';
import UploadFileComponent from './UploadFile';
import { UploadedAttachmentsComponent } from './UploadAttachmentManager/UploadedAttachments';
import { SubmitWithoutDocumentsDialog } from './SubmitWithoutDocumentsDialog';
import { IF } from '../IF';
import { refineFonts } from '@/Theming/refineFonts';
import { EmailSentSuccessfully } from '../EmaillSentSuccessfully/EmailSentSuccessfully';
import { useAnalytics } from '@hooks/useAnalytics';
import { REFINE_ANALYTICS_EVENTS } from '@/types/RefineAnalyticsEvents';

interface IAddSupportingDocumentsProps {
	handleSubmit: Function;
	isSubmitWithoutDocumentsDialogOpen: boolean;
	setIsSubmitWithoutDocumentsDialogOpen: Function;
	setIsFileUploaded: Function;
	isFileUploaded: boolean;
	setIsEmailSent: Function;
}

interface EmailData {
	email: string;
	emailSent: boolean;
}

export const AddSupportingDocumentsComponent: FC<IAddSupportingDocumentsProps> = ({
	handleSubmit,
	setIsSubmitWithoutDocumentsDialogOpen,
	isSubmitWithoutDocumentsDialogOpen,
	setIsFileUploaded,
	isFileUploaded,
	setIsEmailSent,
}) => {
	const [newAttachments, setNewAttachments] = useState<File[]>();

	const [emailData, setEmailData] = useState<EmailData>({
		email: '',
		emailSent: false,
	});

	const [isAreaActive, setIsAreaActive] = useState(false);
	const { trackRefineEvent } = useAnalytics();

	const { FileUpload: text } = useContext(LocalizationContext);

	useEffect(() => {
		const emailDataFromLocalStorage: EmailData = JSON.parse(sessionStorage.getItem('emailData') ?? '{}');
		setEmailData(emailDataFromLocalStorage);
		trackRefineEvent(REFINE_ANALYTICS_EVENTS.UPLOAD_FILES_SCREEN_LOADED);
	}, []);

	useEffect(() => {
		sessionStorage.setItem('emailData', JSON.stringify(emailData));
		setIsEmailSent(emailData.emailSent);
	}, [emailData]);

	const onFileUpload = (files: File[]) => {
		setNewAttachments(files);
		setIsFileUploaded(files.length > 0);
	};

	const ref = useRef<any>(null);
	const styles = getStyles({ isDragActive: isAreaActive });

	const handleSubmitWithoutDocuments = () => {
		setIsSubmitWithoutDocumentsDialogOpen(true);
		trackRefineEvent(REFINE_ANALYTICS_EVENTS.SUBMIT_WITHOUT_DOCUMENTS_CLICKED);
	};

	return (
		<>
			<SubmitWithoutDocumentsDialog
				isDialogOpen={isSubmitWithoutDocumentsDialogOpen}
				setOpenDialog={setIsSubmitWithoutDocumentsDialogOpen}
				handleSubmit={handleSubmit}
			/>
			<IF condition={emailData.emailSent}>
				<EmailSentSuccessfully email={emailData.email} />
			</IF>

			<IF condition={!emailData.emailSent}>
				<Stack overflow={'scroll'} ref={ref} direction="column" sx={styles.container} spacing={3}>
					{/* Header */}
					<Typography
						textAlign={'center'}
						tabIndex={0}
						aria-label={text.AddSupportingDocumentsHeader || 'Add supporting document(s)'}
						variant="questionText">
						{text.AddSupportingDocumentsHeader || 'Add supporting document(s)'}
					</Typography>
					<UploadFileComponent
						containerRef={ref}
						activateContainerDropArea={setIsAreaActive}
						uploadAttachment={onFileUpload}
					/>

					<Box sx={{ display: { xl: 'none', md: 'none', sm: 'block', xs: 'block' } }}>
						<UploadFromComputerComponent setEmailData={setEmailData} />
					</Box>
					<IF condition={!isFileUploaded}>
						<Stack marginTop={'34px'} justifyContent={'center'} alignItems={'center'}>
							<Button
								data-cy={'pqSubmitWithoutDocumentsButton'}
								onClick={handleSubmitWithoutDocuments}
								sx={{ maxWidth: '200px', ...refineFonts.mediumBold }}
								variant={'text'}>
								{text.SubmitWithoutDocumentsButton || 'Submit without documents'}
							</Button>
						</Stack>
					</IF>

					{/* PQ Uploaded Files */}
					<UploadedAttachmentsComponent newAttachments={newAttachments} />
				</Stack>
			</IF>
		</>
	);
};
